/**
 * DS Coder — assembles the coding-assistant system prompt extension for
 * attached GitHub repos: detected tech stack, coding standards, common
 * errors to avoid, golden-stack library rules, and (when the message is
 * code-related) live documentation excerpts for the libraries involved.
 *
 * Deterministic pipeline, no extra LLM calls: detection and pack selection
 * are pure functions; live docs are a bounded, cached, best-effort fetch.
 */

import * as githubService from '@/services/githubService';
import { detectTechStack } from './stackDetector';
import { resolveKnowledgePacks, type ResolvedPacks } from './packsRepository';
import { resolveRules } from './rulesRepository';
import { anyGlobMatches, parseGlobs } from './globMatch';
import { fetchLiveDocsBatch } from './liveDocs';
import type { DetectedStack, KnowledgePack, TechId } from './types';
import type { DsCoderRule } from '@/types/dsCoderRules';

type RepoRef = { owner: string; name: string; fullName: string };

type CachedRepoContextShape = {
  tree?: { tree?: Array<{ path: string; type: string }> };
  languages?: Record<string, number>;
} | null;

const stackCache = new Map<string, DetectedStack>();
const MAX_DOCS_FETCHES = 2;
const DOCS_TOKENS = 2_500;
/** Live docs are only fetched when the message names one of these techs/libs. */
const DOCS_TRIGGER_MIN_MESSAGE_LEN = 8;

export function clearDsCoderStackCache(): void {
  stackCache.clear();
}

async function detectStackForRepo(
  repo: RepoRef,
  cachedContext: CachedRepoContextShape
): Promise<DetectedStack> {
  const cached = stackCache.get(repo.fullName);
  if (cached) return cached;

  let dependencies: githubService.GitHubDependencies | null = null;
  try {
    dependencies = await githubService.getDependencies(repo.owner, repo.name);
  } catch (e) {
    console.warn(`[DS Coder] Dependencies unavailable for ${repo.fullName}:`, e);
  }

  const treePaths = (cachedContext?.tree?.tree ?? [])
    .filter((t) => t.type === 'blob')
    .map((t) => t.path);

  const stack = detectTechStack({
    repoFullName: repo.fullName,
    dependencies,
    treePaths,
    languages: cachedContext?.languages ?? null,
  });
  // Only cache detections that found something — a repo whose metadata was
  // still loading should be re-detected on the next message.
  if (stack.techs.length > 0) stackCache.set(repo.fullName, stack);
  return stack;
}

function packsForStack(stack: DetectedStack, packs: ResolvedPacks): KnowledgePack[] {
  return stack.techs
    .map((t) => packs[t.id])
    .filter((p): p is KnowledgePack => Boolean(p));
}

/**
 * Pick libraries whose docs are worth fetching for this message: the message
 * must look code-related and reference a detected tech (or one of the repo's
 * dependencies) by name. Bounded to MAX_DOCS_FETCHES.
 */
function selectDocsTargets(
  message: string,
  stacks: DetectedStack[],
  packs: ResolvedPacks
): Array<{ libraryId: string; topic?: string }> {
  const text = message.toLowerCase();
  if (text.length < DOCS_TRIGGER_MIN_MESSAGE_LEN) return [];

  const targets: Array<{ libraryId: string; topic?: string; score: number }> = [];
  const seen = new Set<string>();

  for (const stack of stacks) {
    for (const tech of stack.techs) {
      const pack = packs[tech.id];
      if (!pack?.docsLibraryId || seen.has(pack.docsLibraryId)) continue;
      const names = [pack.label.toLowerCase(), tech.id.replace('-', ' ')];
      const mentioned = names.some((n) => text.includes(n));
      if (mentioned) {
        seen.add(pack.docsLibraryId);
        // Use the user's message as the topic so Context7 returns the relevant section.
        targets.push({ libraryId: pack.docsLibraryId, topic: message.slice(0, 200), score: 2 });
      }
    }
  }

  return targets
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_DOCS_FETCHES)
    .map(({ libraryId, topic }) => ({ libraryId, topic }));
}

function renderStackSection(stacks: DetectedStack[]): string {
  const lines: string[] = ['=== DETECTED TECH STACK ==='];
  for (const stack of stacks) {
    if (stack.techs.length === 0) continue;
    const techList = stack.techs
      .map((t) => (t.version ? `${t.label} (${t.version})` : t.label))
      .join(', ');
    lines.push(`${stack.repoFullName}: ${techList}`);
  }
  return lines.length > 1 ? lines.join('\n') : '';
}

function renderPacksSection(packs: KnowledgePack[]): string {
  if (packs.length === 0) return '';
  const parts: string[] = [
    '=== CODING STANDARDS (MUST follow for all code you write or review) ===',
  ];
  for (const pack of packs) {
    parts.push(`\n## ${pack.label}`);
    parts.push(...pack.standards.map((s) => `- ${s}`));
  }

  parts.push('\n=== COMMON ERRORS TO AVOID (known failure modes for this stack) ===');
  for (const pack of packs) {
    const errs = pack.commonErrors;
    if (errs.length === 0) continue;
    parts.push(`\n## ${pack.label}`);
    parts.push(...errs.map((e) => `- ${e.error} → Fix: ${e.fix}`));
  }

  const withLibs = packs.filter((p) => p.goldenLibraries.length > 0);
  if (withLibs.length > 0) {
    parts.push(
      '\n=== GOLDEN STACK LIBRARIES ===',
      'When new functionality needs a package, use these curated picks. Do NOT suggest packages outside this list unless nothing here covers the need — and say so explicitly when you do.'
    );
    for (const pack of withLibs) {
      parts.push(`\n## ${pack.label}`);
      parts.push(
        ...pack.goldenLibraries.map(
          (g) => `- ${g.need}: use ${g.use}${g.avoid ? ` (avoid: ${g.avoid})` : ''}`
        )
      );
    }
  }
  return parts.join('\n');
}

/** Collect every blob path across the attached repos, for glob-based rule targeting. */
function collectTreePaths(
  repos: RepoRef[],
  cachedRepoContexts: Record<string, unknown>
): string[] {
  const paths: string[] = [];
  for (const repo of repos) {
    const ctx = (cachedRepoContexts?.[repo.fullName] ?? null) as CachedRepoContextShape;
    for (const node of ctx?.tree?.tree ?? []) {
      if (node.type === 'blob') paths.push(node.path);
    }
  }
  return paths;
}

/**
 * A rule applies when it is always-apply, OR its globs match a file in an
 * attached repo. A rule with neither globs nor always-apply is a user's own
 * general rule, so it applies whenever a repo is attached.
 */
function selectApplicableRules(rules: DsCoderRule[], treePaths: string[]): DsCoderRule[] {
  return rules.filter((rule) => {
    if (rule.alwaysApply) return true;
    const patterns = parseGlobs(rule.globs);
    if (patterns.length === 0) return true;
    return anyGlobMatches(patterns, treePaths);
  });
}

function renderRulesSection(rules: DsCoderRule[]): string {
  if (rules.length === 0) return '';
  const parts: string[] = [
    '=== PROJECT RULES (user-defined; MUST follow when writing or reviewing code) ===',
  ];
  for (const rule of rules) {
    parts.push(`\n## ${rule.name}`);
    if (rule.description.trim()) parts.push(`_${rule.description.trim()}_`);
    if (rule.content.trim()) parts.push(rule.content.trim());
  }
  return parts.join('\n');
}

export interface DsCoderExtensionResult {
  prompt: string;
  detectedStacks: DetectedStack[];
  liveDocsLibraries: string[];
  appliedRules: string[];
}

/**
 * Build the DS Coder system prompt extension. Never throws — on any failure
 * it degrades to whatever sections could be built (possibly an empty prompt).
 */
export async function buildDsCoderSystemExtension(opts: {
  repos: RepoRef[];
  cachedRepoContexts: Record<string, unknown>;
  lastUserMessageText: string;
  /** Owner context for resolving DB-managed packs (global/workspace/user). */
  userId?: string | null;
  workspaceId?: string | null;
}): Promise<DsCoderExtensionResult> {
  const empty: DsCoderExtensionResult = {
    prompt: '',
    detectedStacks: [],
    liveDocsLibraries: [],
    appliedRules: [],
  };
  if (opts.repos.length === 0) return empty;

  try {
    const cachedRepoContexts = opts.cachedRepoContexts ?? {};
    const [stacks, resolvedPacks, resolvedRules] = await Promise.all([
      Promise.all(
        opts.repos.map((repo) =>
          detectStackForRepo(repo, (cachedRepoContexts?.[repo.fullName] ?? null) as CachedRepoContextShape)
        )
      ),
      resolveKnowledgePacks({ userId: opts.userId, workspaceId: opts.workspaceId }),
      resolveRules({ userId: opts.userId, workspaceId: opts.workspaceId }),
    ]);
    const nonEmptyStacks = stacks.filter((s) => s.techs.length > 0);

    // User-authored rules apply whenever a repo is attached, independent of
    // whether a stack was detected (glob rules are matched against repo files).
    const treePaths = collectTreePaths(opts.repos, cachedRepoContexts);
    const applicableRules = selectApplicableRules(resolvedRules, treePaths);

    // Nothing to add if no stack was detected and no rule applies.
    if (nonEmptyStacks.length === 0 && applicableRules.length === 0) return empty;

    // Dedupe packs across repos, preserving detection order.
    const packMap = new Map<TechId, KnowledgePack>();
    for (const stack of nonEmptyStacks) {
      for (const pack of packsForStack(stack, resolvedPacks)) {
        if (!packMap.has(pack.id)) packMap.set(pack.id, pack);
      }
    }
    const packs = [...packMap.values()];

    const sections = [
      'DS CODER MODE: The user attached the repository/repositories below. Act as a senior engineer on this codebase: match its existing conventions, and apply the standards, error-avoidance rules, library policy, and project rules that follow.',
      renderStackSection(nonEmptyStacks),
      renderPacksSection(packs),
      renderRulesSection(applicableRules),
    ].filter(Boolean);

    // Live docs: bounded, cached, best-effort. Only relevant with a detected stack.
    let liveDocsLibraries: string[] = [];
    if (nonEmptyStacks.length > 0) {
      const docsTargets = selectDocsTargets(opts.lastUserMessageText, nonEmptyStacks, resolvedPacks);
      if (docsTargets.length > 0) {
        const docs = await fetchLiveDocsBatch(
          docsTargets.map((t) => ({ ...t, tokens: DOCS_TOKENS }))
        );
        if (docs.length > 0) {
          liveDocsLibraries = docs.map((d) => d.libraryId);
          const docParts = [
            '=== LIVE DOCUMENTATION (fetched now — authoritative over your training data) ===',
            'Use these current docs for API signatures and patterns. If your memory of an API conflicts with this documentation, the documentation wins.',
          ];
          for (const doc of docs) {
            docParts.push(`\n## ${doc.libraryId}${doc.topic ? ` — topic: ${doc.topic.slice(0, 80)}` : ''}\n${doc.content}`);
          }
          sections.push(docParts.join('\n'));
        }
      }
    }

    return {
      prompt: sections.join('\n\n'),
      detectedStacks: nonEmptyStacks,
      liveDocsLibraries,
      appliedRules: applicableRules.map((r) => r.name),
    };
  } catch (e) {
    console.warn('[DS Coder] Failed to build coder context:', e);
    return empty;
  }
}
